import type { ExpenseFundingSource, ExpenseStatus, ExpenseStepStatus, KnownPermissionName } from '@/types/db'

export function fmtVnd(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${Number(n).toLocaleString('vi-VN')} đ`
}

export function fmtDateTimeVi(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export const EXPENSE_STATUS_LABEL: Record<ExpenseStatus, string> = {
  draft: 'Nháp',
  pending: 'Đang duyệt',
  paid: 'Đã chi',
  archived: 'Đã lưu trữ',
  rejected: 'Bị trả lại',
  cancelled: 'Đã huỷ',
}

// Class Tailwind cho badge trạng thái (nền nhạt + chữ đậm).
// Đồng bộ bảng màu với component Badge (slate/sky/emerald/red) để nhất quán toàn app.
export const EXPENSE_STATUS_TONE: Record<ExpenseStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  pending: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  paid: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  archived: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  rejected: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
}

export const FUNDING_LABEL: Record<ExpenseFundingSource, string> = {
  company_account: 'TK công ty',
  personal_warehouse: 'TK cá nhân Thủ kho',
  personal_shipping: 'TK cá nhân Vận đơn',
  personal_treasurer: 'TK cá nhân Thủ quỹ',
  cash: 'Tiền mặt',
}

// Thứ tự hiện ở ô chọn nguồn tiền (form tạo/sửa).
export const FUNDING_SOURCES = [
  'company_account',
  'personal_warehouse',
  'personal_shipping',
  'personal_treasurer',
  'cash',
] as const satisfies ReadonlyArray<ExpenseFundingSource>

// Quyền riêng của TỪNG nguồn tiền (xem + chi phiếu nguồn đó) — migration
// 20261005_0003. Khớp hàm DB expense_fund_permission — sửa thì sửa CẢ 2.
// Nguồn lạ → null.
export function fundPermissionFor(src: string | null | undefined): KnownPermissionName | null {
  switch (src) {
    case 'company_account':
      return 'expense:fund_company'
    case 'cash':
      return 'expense:fund_cash'
    case 'personal_warehouse':
      return 'expense:fund_warehouse'
    case 'personal_shipping':
      return 'expense:fund_shipping'
    case 'personal_treasurer':
      return 'expense:fund_treasurer'
    default:
      return null
  }
}

// Tiền mặt + mọi TK cá nhân → luồng 'cash', chứng từ "Chứng từ chi" (CT).
export function isCashKindSource(src: string | null | undefined): boolean {
  return src === 'cash' || (src?.startsWith('personal_') ?? false)
}

export const STEP_STATUS_LABEL: Record<ExpenseStepStatus, string> = {
  pending: 'Đang chờ',
  approved: 'Đã duyệt',
  auto_approved: 'Tự duyệt (trùng người)',
  rejected: 'Từ chối / trả lại',
  skipped: 'Bỏ qua',
}

// Bước "Theo quyền" trong cấu hình luồng — nhãn thân thiện thay cho tên quyền kỹ thuật.
export const APPROVAL_PERMISSION_OPTIONS: { value: string; label: string }[] = [
  { value: 'expense:approve', label: 'Cấp duyệt (người được quyền duyệt chi)' },
  // Giá trị ĐÁNH DẤU (không phải quyền thật): DB thay bằng quyền nguồn tiền của phiếu
  // lúc gửi duyệt (expense_build_steps) — bước chi do người giữ quỹ nguồn đó làm.
  { value: 'expense:fund', label: 'Người giữ quỹ của nguồn tiền' },
  { value: 'expense:create_command', label: 'Người tạo lệnh chi' },
]

// 5 quyền nguồn tiền (xem + chi). Ai có ít nhất 1 quyền này là người giữ quỹ.
export const EXPENSE_FUND_PERMISSIONS = FUNDING_SOURCES.map(
  (s) => fundPermissionFor(s) as KnownPermissionName,
)

export function approvalPermissionLabel(perm: string | null): string {
  const found = APPROVAL_PERMISSION_OPTIONS.find((o) => o.value === perm)
  if (found) return found.label
  // Bước chi đã materialize mang quyền nguồn thật (vd expense:fund_company).
  const src = FUNDING_SOURCES.find((s) => fundPermissionFor(s) === perm)
  if (src) return `Người giữ quỹ ${FUNDING_LABEL[src]}`
  return perm ?? ''
}

// Trang mở 1 file chứng từ — link trong file Excel tab Phiếu chi trỏ vào đây.
export function expenseFilePath(attachmentId: number): string {
  return `/expenses/files/${attachmentId}`
}

export function mimeIcon(mime: string | null): string {
  if (!mime) return 'FILE'
  if (mime.includes('pdf')) return 'PDF'
  if (mime.includes('xml')) return 'XML'
  if (mime.startsWith('image/')) return 'IMG'
  return 'FILE'
}
